import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { BOOKMAKER_LOGOS, ALL_BOOKMAKERS } from '../config/bookmakers'
import { ALL_SPORTS, eventLabel, eventLabelShort } from '../config/sports'
import { MARKET_TYPE_MAPPING, MARKET_TYPE_FILTER_MAPPING } from '../config/markets'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import FilterPanel, { DEFAULT_FILTERS } from './FilterPanel'
import TrackBetModal, { useTrackBet } from './TrackBetModal'
import HiddenBetsModal, { useHiddenBets } from './HiddenBetsModal'
import EVMultiBuilder from './EVMultiBuilder'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import SubjectImage from '../components/SubjectImage'
import EVTour, { openEvTour } from './evTour'
import ShareModal from './ShareModal'
import '../styles/app.css'
import './EVNew.css'
import PreviewWall from '../components/PreviewWall'
import useVisiblePolling from '../hooks/useVisiblePolling'

// Saved-filter format (kept compatible with legacy EV8 records in user_filters table):
//   filters.sports               = whitelist of selected leagues  (e.g. ['NBA', 'AFL'])
//   filters.bookmakers           = whitelist of selected bookies  (e.g. ['Bet365', 'Sportsbet'])
//   filters.excludedMarketTypes  = blacklist of market display names (e.g. ['Player Points'])
// EVNew's runtime state uses excluded* sets for everything; we convert at the save/load boundary.
const PAGE_TYPE = 'ev'

export default function EVNew({ isVisible = true, isPreview = false }) {
  const { user, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('ev')

  const [opportunities, setOpportunities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)

  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [filters, setFilters] = useState(() => ({
    excludedBookmakers:  new Set(),
    excludedMarketTypes: new Set(),
    excludedSports:      new Set(),
    odds:       DEFAULT_FILTERS.odds,
    ev:         DEFAULT_FILTERS.ev,
    hoursUntil: DEFAULT_FILTERS.hoursUntil,
    minBooks:   DEFAULT_FILTERS.minBooks,
  }))
  const [kellyFraction, setKellyFraction] = useState('1/2')
  const [bankroll, setBankroll] = useState(1000)
  const [roundStakes, setRoundStakes] = useState('5')

  const [savedFilters, setSavedFilters] = useState([])
  const [filterName, setFilterName] = useState('')
  const [activeFilterId, setActiveFilterId] = useState(null)
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)

  const {
    hiddenBets,
    hideOpp,
    unhideOpp,
    clearAll: clearAllHidden,
    isHidden,
    showModal: showHiddenModal,
    setShowModal: setShowHiddenModal,
  } = useHiddenBets()

  const {
    isTracked,
    openTrackModal,
    closeTrackModal,
    saveTrack,
    showTrackModal,
    trackingOpp,
    trackForm,
    setTrackForm,
  } = useTrackBet({ kellyFraction, bankroll, roundStakes, hideOpp })

  // Multi Builder state
  const [showMultiModal, setShowMultiModal] = useState(false)
  const [selectedLegs,   setSelectedLegs]   = useState([])
  const [multiError,     setMultiError]     = useState(null) // { key, msg }
  const [pickerState,    setPickerState]    = useState(null) // { opp, x, y }

  const flashMultiError = (key, msg) => {
    setMultiError({ key, msg })
    setTimeout(() => setMultiError(prev => (prev?.key === key ? null : prev)), 2800)
  }

  // Sort + pagination + auto-refresh
  const [sortBy,             setSortBy]             = useState('ev_percentage')
  const [sortDir,            setSortDir]            = useState('desc')
  const [evMode,             setEvMode]             = useState('pct') // 'pct' | 'dollar'
  const [currentPage,        setCurrentPage]        = useState(1)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)
  const [shareBet,           setShareBet]           = useState(null)
  const itemsPerPage = 25

  // Compute the rounded stake amount (number) from kelly_stake. Returns null if unavailable.
  const computeStakeAmount = (kellyStake) => {
    if (typeof kellyStake !== 'number') return null
    const fractions = { '1/4': 0.25, '1/2': 0.5, '3/4': 0.75, 'Full': 1 }
    const frac = fractions[kellyFraction] ?? 0.5
    const stake = (kellyStake / 100) * frac * bankroll
    if (roundStakes === '1') return Math.round(stake)
    if (roundStakes === '5') return Math.round(stake / 5) * 5
    return Math.round(stake * 100) / 100
  }

  // Compute stake from kelly_stake using user's Kelly fraction + bankroll + rounding (mirrors PROP_EV).
  const computeStake = (kellyStake) => {
    const amt = computeStakeAmount(kellyStake)
    return amt == null ? '' : `$${amt}`
  }

  // Numeric dollar EV = displayed (rounded) stake × EV%. null when unavailable.
  const dollarEvAmount = (opp) => {
    const stake = computeStakeAmount(opp.kelly_stake)
    if (stake == null || typeof opp.ev_percentage !== 'number') return null
    return stake * (opp.ev_percentage / 100)
  }

  // Dollar EV string. Ties out with the visible Stake column.
  const computeDollarEv = (opp) => {
    const ev = dollarEvAmount(opp)
    if (ev == null) return ''
    return `${ev >= 0 ? '+' : '−'}$${Math.abs(ev).toFixed(2)}`
  }

  // Expanded rows
  const [expandedRows,  setExpandedRows]  = useState(new Set())
  const toggleExpand = (key) => setExpandedRows(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const formatTimeWithSeconds = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString + 'Z').toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
    })
  }

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortBy(col); setSortDir('desc') }
  }

  const legKey = (opp) => `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`

  const isInMulti = (opp) => selectedLegs.some(l => l._key === legKey(opp))

  // Add a leg with a chosen bookmaker. Picks the matching game_url from opp.game_urls if available.
  const addLeg = (opp, bookmaker) => {
    const rowBooks = opp.bookmakers && opp.bookmakers.length > 0 ? opp.bookmakers : [opp.bookmaker]
    const bookIdx  = rowBooks.indexOf(bookmaker)
    setSelectedLegs(prev => [...prev, {
      _key: legKey(opp),
      selection:     opp.selection,
      match:         opp.match,
      market_type:   opp.market_type,
      sport:         opp.sport,
      commence_time: opp.commence_time,
      bookmaker,
      game_url:      opp.game_urls?.[bookIdx] || opp.game_url || null,
      odds:          opp.original_odds,
      ev_percentage: opp.ev_percentage,
      // Teams and the raw values behind the selection, so a multi's legs can be
      // settled against results without parsing the display string back apart.
      ...settlementFields(opp),
    }])
  }

  // Toggles a row in/out of the multi. Mirrors PROP_EV:
  //   - Already in multi → remove silently
  //   - Correlated (same selection + market + game) → inline error tooltip
  //   - First leg with multiple bookmakers → open picker so user chooses which book to lock to
  //   - Subsequent legs whose row doesn't offer the locked book → "Not on {bookmaker}" tooltip
  const toggleLeg = (opp, buttonEl) => {
    const key = legKey(opp)
    if (isInMulti(opp)) {
      setSelectedLegs(prev => prev.filter(l => l._key !== key))
      return
    }

    // Correlation check
    const baseMarket = (opp.market_type || '').replace('_milestones', '')
    const correlated = selectedLegs.some(l =>
      l.commence_time === opp.commence_time &&
      l.selection === opp.selection &&
      (l.market_type || '').replace('_milestones', '') === baseMarket
    )
    if (correlated) {
      flashMultiError(key, '⚠ Already in multi (correlated)')
      return
    }

    const rowBooks = opp.bookmakers && opp.bookmakers.length > 0 ? opp.bookmakers : [opp.bookmaker]

    // First leg
    if (selectedLegs.length === 0) {
      if (rowBooks.length === 1) {
        addLeg(opp, rowBooks[0])
      } else if (buttonEl) {
        const rect = buttonEl.getBoundingClientRect()
        setPickerState({ opp, x: rect.left, y: rect.bottom + 4 })
      } else {
        addLeg(opp, rowBooks[0])
      }
      return
    }

    // Subsequent legs — must include the locked bookmaker
    const required = selectedLegs[0].bookmaker
    if (!rowBooks.includes(required)) {
      flashMultiError(key, `Not on ${required}`)
      return
    }
    addLeg(opp, required)
  }

  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('ev-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setLastUpdate(new Date())
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    fetchOpportunities()
  }, [])

  // Load saved filters list
  useEffect(() => {
    if (!user?.id) return
    getUserFilters(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({
        id: f.id,
        name: f.filter_name,
        isDefault: f.is_default || false,
        ...f.filter_data
      })))
    })
  }, [user?.id])

  // Apply default filter on first load
  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, PAGE_TYPE).then(({ data }) => {
      if (data) applyFilterData(data.filter_data)
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  // Convert saved filter_data (whitelist sports/bookies) → EVNew runtime state (excluded sets).
  // Treat undefined OR empty whitelist as "everything selected" (matches legacy EV8 behaviour
  // where an empty sports array meant "show all").
  const applyFilterData = (fd) => {
    // Prefer the saved excludedSports blacklist (newer filters) so leagues added after the
    // filter was saved stay visible. Fall back to the legacy sports whitelist for old filters.
    const excludedSports = Array.isArray(fd.filters.excludedSports)
      ? fd.filters.excludedSports
      : ((Array.isArray(fd.filters.sports) && fd.filters.sports.length > 0)
          ? ALL_SPORTS.filter(s => !fd.filters.sports.includes(s))
          : [])

    const bookmakersWhitelist = fd.filters.bookmakers
    const excludedBookmakers = (Array.isArray(bookmakersWhitelist) && bookmakersWhitelist.length > 0)
      ? ALL_BOOKMAKERS.filter(b => !bookmakersWhitelist.includes(b))
      : []

    setFilters({
      excludedSports:      new Set(excludedSports),
      excludedBookmakers:  new Set(excludedBookmakers),
      excludedMarketTypes: new Set(fd.filters.excludedMarketTypes || []),
      odds:       fd.filters.odds       || DEFAULT_FILTERS.odds,
      ev:         fd.filters.ev         || DEFAULT_FILTERS.ev,
      hoursUntil: fd.filters.hoursUntil || DEFAULT_FILTERS.hoursUntil,
      minBooks:   fd.filters.minBooks   ?? DEFAULT_FILTERS.minBooks,
    })
    if (fd.stakeSettings) {
      setKellyFraction(fd.stakeSettings.kellyFraction)
      setBankroll(fd.stakeSettings.bankroll)
      setRoundStakes(fd.stakeSettings.roundStakes)
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    // Convert EVNew runtime state (excluded sets) → saved filter_data (whitelist sports/bookies)
    const sportsWhitelist     = ALL_SPORTS.filter(s => !filters.excludedSports.has(s))
    const bookmakersWhitelist = ALL_BOOKMAKERS.filter(b => !filters.excludedBookmakers.has(b))

    const fd = {
      filters: {
        sports:              sportsWhitelist,                  // legacy whitelist (kept for back-compat)
        excludedSports:      [...filters.excludedSports],      // blacklist = source of truth on load
        bookmakers:          bookmakersWhitelist,
        excludedMarketTypes: [...filters.excludedMarketTypes],
        odds: filters.odds,
        ev: filters.ev,
        hoursUntil: filters.hoursUntil,
        minBooks: filters.minBooks,
      },
      stakeSettings: { kellyFraction, bankroll, roundStakes },
      createdAt: new Date().toISOString()
    }
    const { data, error } = await saveUserFilter(user.id, PAGE_TYPE, name.trim(), fd)
    if (error) {
      alert('Error saving filter: ' + error)
      return
    }
    if (data) {
      setSavedFilters(prev => [...prev, {
        id: data.id,
        name: data.filter_name,
        isDefault: false,
        ...data.filter_data
      }])
    }
    setFilterName('')
  }

  const handleLoadFilter = (saved) => {
    applyFilterData(saved)
    setActiveFilterId(saved.id)
  }

  const handleDeleteFilter = async (id) => {
    if (!user?.id) return
    const { error } = await deleteUserFilter(id)
    if (error) {
      alert('Error deleting filter: ' + error)
      return
    }
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }

  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) {
      const { error } = await unsetDefaultFilter(user.id, PAGE_TYPE, id)
      if (error) { alert('Error unsetting default: ' + error); return }
    } else {
      const { error } = await setDefaultFilter(user.id, PAGE_TYPE, id)
      if (error) { alert('Error setting default: ' + error); return }
    }
    setSavedFilters(prev => prev.map(f => ({
      ...f,
      isDefault: f.id === id ? !current?.isDefault : false
    })))
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString + 'Z').toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatMarketType = (marketType) => {
    if (MARKET_TYPE_MAPPING[marketType]) return MARKET_TYPE_MAPPING[marketType]
    return marketType
  }

  // "Texas Rangers Over 0.5 Total Runs" → "Over 0.5". Falls back to original for moneyline/team selections.
  const shortSideLabel = (selection) => {
    if (!selection) return ''
    const m = selection.match(/(over|under)\s+(-?\d+(?:\.\d+)?)/i)
    if (m) return `${m[1].charAt(0).toUpperCase()}${m[1].slice(1).toLowerCase()} ${m[2]}`
    return selection
  }

  const evBadgeClass = (opp) => {
    // In $ mode, color by dollar return scaled to bankroll (so bands work at any bankroll).
    if (evMode === 'dollar') {
      const ev = dollarEvAmount(opp)
      if (ev == null || !bankroll) return 'bc-ev-badge bc-ev-low'
      const pctOfRoll = (ev / bankroll) * 100
      if (pctOfRoll >= 0.5)  return 'bc-ev-badge bc-ev-high'   // gold
      if (pctOfRoll >= 0.24) return 'bc-ev-badge bc-ev-mid'    // green
      return 'bc-ev-badge bc-ev-low'                           // blue
    }
    const ev = opp.ev_percentage
    if (ev >= 10) return 'bc-ev-badge bc-ev-high'
    if (ev >= 5)  return 'bc-ev-badge bc-ev-mid'
    return 'bc-ev-badge bc-ev-low'
  }

  // Build set of raw market types excluded based on display-name selections
  const excludedRawMarketTypes = useMemo(() => {
    const out = new Set()
    filters.excludedMarketTypes.forEach(displayName => {
      const rawTypes = MARKET_TYPE_FILTER_MAPPING[displayName]
      if (rawTypes) rawTypes.forEach(t => out.add(t))
    })
    return out
  }, [filters.excludedMarketTypes])

  const filteredOpportunities = useMemo(() => {
    const now = new Date()
    const filtered = opportunities.filter(opp => {
      if (filters.excludedSports.has(opp.sport)) return false
      if (filters.excludedBookmakers.has(opp.bookmaker)) return false
      if (excludedRawMarketTypes.has(opp.market_type)) return false

      const odds = opp.original_odds
      if (typeof odds === 'number' && (odds < filters.odds[0] || odds > filters.odds[1])) return false

      const ev = opp.ev_percentage
      if (typeof ev === 'number' && (ev < filters.ev[0] || ev > filters.ev[1])) return false

      if (opp.commence_time) {
        const gameTime = new Date(opp.commence_time + 'Z')
        const hoursUntil = (gameTime - now) / (1000 * 60 * 60)
        if (hoursUntil < filters.hoursUntil[0] || hoursUntil > filters.hoursUntil[1]) return false
      }

      if (typeof opp.bookmaker_count === 'number' && opp.bookmaker_count < filters.minBooks) return false

      return true
    })

    // Group rows that share match + market + selection + odds + EV — collapses
    // multiple bookmakers offering the same line/odds into a single row with logos.
    const groupedMap = new Map()
    filtered.forEach(opp => {
      const evKey = typeof opp.ev_percentage === 'number' ? opp.ev_percentage.toFixed(2) : ''
      const key = `${opp.match}_${opp.market_type}_${opp.selection}_${opp.original_odds}_${evKey}`
      if (groupedMap.has(key)) {
        groupedMap.get(key).bookmakers.push(opp.bookmaker)
        groupedMap.get(key).game_urls.push(opp.game_url)
      } else {
        groupedMap.set(key, {
          ...opp,
          bookmakers: [opp.bookmaker],
          game_urls: [opp.game_url],
          groupKey: key,
        })
      }
    })
    const grouped = Array.from(groupedMap.values())

    // Sort grouped rows. Mirrors EV8's sort columns: ev_percentage / original_odds
    // / commence_time / sport. String sort uses localeCompare; everything else numeric.
    return grouped.sort((a, b) => {
      let av, bv
      if (sortBy === 'ev_percentage') {
        // In $ mode, rank by dollar EV (stake × EV%) instead of raw EV%.
        if (evMode === 'dollar') {
          const dollarEv = (o) => {
            const stake = computeStakeAmount(o.kelly_stake)
            return stake == null || typeof o.ev_percentage !== 'number' ? -Infinity : stake * (o.ev_percentage / 100)
          }
          av = dollarEv(a); bv = dollarEv(b)
        } else { av = a.ev_percentage; bv = b.ev_percentage }
      }
      else if (sortBy === 'original_odds') { av = a.original_odds; bv = b.original_odds }
      else if (sortBy === 'kelly_stake')   { av = a.kelly_stake;   bv = b.kelly_stake }
      else if (sortBy === 'commence_time') {
        av = new Date(a.commence_time + 'Z').getTime()
        bv = new Date(b.commence_time + 'Z').getTime()
      }
      else if (sortBy === 'sport') {
        return sortDir === 'desc' ? b.sport.localeCompare(a.sport) : a.sport.localeCompare(b.sport)
      }
      else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })
    // computeStakeAmount is an inline fn (new identity each render); its inputs are already in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunities, filters, excludedRawMarketTypes, sortBy, sortDir, evMode, kellyFraction, bankroll, roundStakes])

  // Hide rows the user has explicitly hidden (also re-filters when hiddenBets changes).
  const visibleOpportunities = useMemo(
    () => filteredOpportunities.filter(opp => !isHidden(opp)),
    // isHidden closes over hiddenBets (already a dep); listing it would recompute every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredOpportunities, hiddenBets]
  )

  // Pagination — reset to page 1 whenever the visible list shrinks past the cursor
  const totalPages = Math.max(1, Math.ceil(visibleOpportunities.length / itemsPerPage))
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1)
  }, [totalPages, currentPage])
  const pagedOpps = isPreview
    ? visibleOpportunities.slice(0, 4)
    : visibleOpportunities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Tick every 10s so the live-data dot can re-evaluate freshness without depending on a refetch.
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  const dataAgeSec = lastUpdate ? (new Date() - lastUpdate) / 1000 : null
  const dotClass = !lastUpdate ? 'bc-live-dot' : dataAgeSec < 60 ? 'bc-live-dot' : 'bc-live-dot stale'

  // Auto-refresh: every 30s while enabled and the page is visible
  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  if (!isVisible) return null

  return (
    <div className="bc-container tool-unified tool-refined ev-unified">

      {/* Header */}
      <div className="bc-header">
        <div className="bc-header-titlewrap">
          <div className="bc-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <div className="bc-header-textcol">
            <div className="bc-header-titlerow">
              <h1 className="bc-title">Positive Expected Value</h1>
              <button className="bc-info-btn" onClick={openEvTour} aria-label="How this works" data-tooltip="How this works">?</button>
              <span className={dotClass} data-tooltip={lastUpdate ? `Last refreshed ${formatTimeWithSeconds(lastUpdate.toISOString().replace('Z',''))}` : 'No data yet'} />
            </div>
          </div>
        </div>
        <div className="bc-header-actions">
          {!isPreview && (
            <button
              className={`bc-btn bc-btn-toggle${selectedLegs.length > 0 ? ' toggle-active' : ''}`}
              onClick={() => setShowMultiModal(true)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
              </svg>
              Multi
              {selectedLegs.length > 0 && (
                <span className="bc-multi-count">{selectedLegs.length}</span>
              )}
            </button>
          )}

          <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filters
          </button>

          {Object.keys(hiddenBets).length > 0 && (
            <button className="bc-btn" onClick={() => setShowHiddenModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
              {Object.keys(hiddenBets).length}
            </button>
          )}

          <button
            className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
            onClick={() => setAutoRefreshEnabled(v => !v)}
          >
            Auto
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>

          <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {error && <div className="bc-error">Error: {error}</div>}

      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {!loading && (
        <div className="bc-table-wrapper">
          <div className="bc-table-scroll">
            <table className="bc-table">
              <thead>
                <tr>
                  {[
                    { label: '',          key: null,            sortable: false },
                    { label: 'Match',     key: 'commence_time', sortable: true  },
                    { label: 'Market',    key: null,            sortable: false },
                    { label: 'Selection', key: null,            sortable: false },
                    { label: 'Odds',      key: 'original_odds', sortable: true  },
                    { label: 'EV',        key: 'ev_percentage', sortable: true, evToggle: true },
                    { label: 'Stake',     key: 'kelly_stake',   sortable: true  },
                    { label: '',          key: null,            sortable: false },
                  ].map((col, i) => (
                    <th
                      key={i}
                      className={`${col.sortable ? 'sortable' : ''}${sortBy === col.key ? ' sorted' : ''}`}
                      onClick={() => col.sortable && handleSort(col.key)}
                      style={{ cursor: col.sortable ? 'pointer' : 'default' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {col.label}
                        {col.evToggle && (
                          <span
                            className="bc-ev-toggle"
                            data-tooltip="Show EV as % or $"
                            onClick={e => { e.stopPropagation(); setEvMode(m => m === 'pct' ? 'dollar' : 'pct') }}
                          >
                            <span className={evMode === 'pct' ? 'active' : ''}>%</span>
                            <span className={evMode === 'dollar' ? 'active' : ''}>$</span>
                          </span>
                        )}
                        {col.sortable && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={sortBy === col.key ? '#60a5fa' : '#334155'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            {sortBy === col.key
                              ? (sortDir === 'desc'
                                  ? <polyline points="6 9 12 15 18 9"/>
                                  : <polyline points="18 15 12 9 6 15"/>)
                              : <><polyline points="6 9 12 4 18 9"/><polyline points="6 15 12 20 18 15"/></>
                            }
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedOpps.map((opp, idx) => {
                  const inMulti = isInMulti(opp)
                  const legIdx  = inMulti ? selectedLegs.findIndex(l => l._key === legKey(opp)) : -1
                  const rowKey  = legKey(opp)
                  const isExpanded = expandedRows.has(rowKey)
                  const blurRow  = isPreview && idx > 0
                  const blurCell = blurRow ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}
                  return (
                  <React.Fragment key={rowKey}>
                  <tr className={`bc-row${inMulti ? ' bc-row-in-multi' : ''}${isExpanded ? ' bc-row-expanded' : ''}`}>
                    <td style={blurCell}>
                      <div className="bc-multi-btn-wrap">
                        <button
                          className={`bc-multi-btn${inMulti ? ' in-multi' : ''}`}
                          onClick={(e) => !isPreview && toggleLeg(opp, e.currentTarget)}
                          disabled={isPreview}
                          data-tooltip={inMulti ? 'Remove from multi' : 'Add to multi'}
                        >
                          {inMulti && (
                            <span style={{ fontSize: '11px', fontWeight: 700, lineHeight: 1 }}>{legIdx + 1}</span>
                          )}
                        </button>
                        {multiError?.key === rowKey && (
                          <div className="bc-multi-err-tip">{multiError.msg}</div>
                        )}
                      </div>
                    </td>
                    <td style={blurCell}>
                      {/* Sport rides under the match as a small tag beside the
                          kick-off, rather than taking a column of its own. The
                          full label (tour + event for tennis) is on hover. */}
                      <div className="bc-cell-text bc-match-name">{opp.match}</div>
                      <div className="bc-match-meta">
                        <span className="bc-sport-tag" title={eventLabel(opp)}>{eventLabelShort(opp)}</span>
                        <span className="bc-match-time">{formatDateTime(opp.commence_time)}</span>
                      </div>
                    </td>
                    <td style={blurCell}>
                      <span className="bc-cell-text bc-market-text">{formatMarketType(opp.market_type)}</span>
                    </td>
                    <td style={blurCell}>
                      {/* Headshot for a player prop, crest for a team market, both
                          crests for a match total — the tracker's own rules, imported
                          rather than restated so the two screens can never disagree.
                          settlementFields() is what supplies home/away (split off
                          `match`); the raw opportunity has no such fields, so passing
                          it straight in would silently drop every match total. */}
                      <div className="bc-ev-sel-row">
                        <SubjectImage opp={opp} />
                        <span className="bc-cell-selection" style={isPreview ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>{opp.selection}</span>
                      </div>
                    </td>
                    <td style={blurCell}>
                      {/* Bookmakers and their price in one cell: the odds are
                          what those books are offering, so they read as a unit. */}
                      <div className="bc-price-cell">
                      <div className="bc-book-cell">
                        {(opp.bookmakers && opp.bookmakers.length > 0 ? opp.bookmakers : [opp.bookmaker]).map((bookmaker, i) => {
                          const url = opp.game_urls?.[i] || opp.game_url
                          const logo = BOOKMAKER_LOGOS[bookmaker]
                          return (
                            <div key={`${bookmaker}-${i}`} className="bc-book-logo-wrap" data-tooltip={bookmaker}>
                              {logo ? (
                                <img
                                  src={logo}
                                  alt={bookmaker}
                                  className="bc-book-logo"
                                  onClick={() => url && window.open(url, '_blank')}
                                />
                              ) : (
                                <button
                                  className="bc-book-row-abbr"
                                  onClick={() => url && window.open(url, '_blank')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {bookmaker.slice(0, 2).toUpperCase()}
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <span className="bc-cell-text bc-price-odds" style={isPreview ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>{opp.original_odds?.toFixed(2)}</span>
                      </div>
                    </td>
                    <td style={blurCell}>
                      <span className={evBadgeClass(opp)}>
                        {evMode === 'dollar' ? computeDollarEv(opp) : `${opp.ev_percentage?.toFixed(2)}%`}
                      </span>
                    </td>
                    <td style={blurCell}>
                      <span className="bc-cell-text">{computeStake(opp.kelly_stake)}</span>
                    </td>
                    <td style={{ textAlign: 'center', ...blurCell }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        {!isPreview && (
                          <button
                            className={`bc-expand-btn track-btn${isTracked(opp) ? ' tracked' : ''}`}
                            onClick={() => !isTracked(opp) && openTrackModal(opp)}
                            data-tooltip={isTracked(opp) ? 'Tracked' : 'Track bet'}
                          >
                            {isTracked(opp) ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                            )}
                          </button>
                        )}
                        {!isPreview && (
                          <button
                            className="bc-expand-btn"
                            onClick={() => setShareBet(opp)}
                            data-tooltip="Share"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="18" cy="5" r="3"/>
                              <circle cx="6" cy="12" r="3"/>
                              <circle cx="18" cy="19" r="3"/>
                              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                            </svg>
                          </button>
                        )}
                        {!isPreview && (
                          <button
                            className="bc-expand-btn hide-btn"
                            onClick={() => hideOpp(opp)}
                            data-tooltip="Hide"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          </button>
                        )}
                        <button
                          className={`bc-expand-btn${isPreview ? ' locked' : ''}${isExpanded ? ' active' : ''}`}
                          onClick={() => !isPreview && toggleExpand(rowKey)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            {isExpanded
                              ? <polyline points="18 15 12 9 6 15"/>
                              : <polyline points="6 9 12 15 18 9"/>}
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (() => {
                    const bookEntries = Object.entries(opp.all_bookmaker_odds || {})
                      .sort((a, b) => (b[1].odds || 0) - (a[1].odds || 0))
                    const hasOpposing  = bookEntries.some(([, d]) => typeof d.opposing_odds === 'number')
                    // Milestones (Over-only): the picked book is intentionally excluded from the
                    // grid, so don't flag any paired book as "best" on the PICKED side — it would
                    // imply a paired book beats the pick. The opposing (Under) side is a genuine
                    // comparison set, so its best-highlight stays.
                    const isMilestone  = opp.is_milestone || (opp.market_type || '').endsWith('_milestones')
                    const topOdds      = bookEntries[0]?.[1].odds
                    const bestSet      = isMilestone
                      ? new Set()
                      : new Set(bookEntries.filter(([, d]) => d.odds === topOdds).map(([b]) => b))
                    const topOpposing  = hasOpposing ? Math.max(...bookEntries.map(([, d]) => d.opposing_odds || 0)) : null
                    const bestOpposing = hasOpposing
                      ? new Set(bookEntries.filter(([, d]) => d.opposing_odds === topOpposing).map(([b]) => b))
                      : new Set()
                    const oppEntry     = hasOpposing ? bookEntries.find(([, d]) => d.opposing_selection) : null
                    const pickedLabel  = shortSideLabel(opp.selection)
                    const pairedLabel  = oppEntry ? shortSideLabel(oppEntry[1].opposing_selection) : 'Paired'
                    return (
                      <tr>
                        <td className="bc-expanded-td" colSpan={8} style={{ maxWidth: 0 }}>
                          <div className="bc-expanded-content">

                            {/* Bookmaker odds — prop_ev style: label column + 1 column per book, 2 rows */}
                            {bookEntries.length > 0 && (
                              <div className="bc-expanded-grid-scroll">
                              <div
                                className="bc-expanded-aligned-grid"
                                style={{ gridTemplateColumns: `90px repeat(${bookEntries.length}, auto)` }}
                              >
                                {/* Picked side row */}
                                <span className="bc-expanded-line-label">{pickedLabel}</span>
                                {bookEntries.map(([book, data]) => {
                                  const isBest = bestSet.has(book)
                                  return (
                                    <div key={`picked-${book}`} className={`bc-odds-chip${isBest ? ' is-best' : ''}`} title={typeof data.margin === 'number' ? `${data.margin.toFixed(2)}% margin` : undefined}>
                                      {BOOKMAKER_LOGOS[book]
                                        ? <img src={BOOKMAKER_LOGOS[book]} alt={book} className="bc-odds-chip-logo" data-tooltip={book} />
                                        : <span className="bc-odds-chip-name">{book}</span>}
                                      <span className="bc-odds-chip-val">{typeof data.odds === 'number' ? data.odds.toFixed(2) : '—'}</span>
                                    </div>
                                  )
                                })}

                                {/* Paired side row */}
                                {hasOpposing && <>
                                  <span className="bc-expanded-line-label">{pairedLabel}</span>
                                  {bookEntries.map(([book, data]) => {
                                    const isBestOpp = bestOpposing.has(book)
                                    return (
                                      <div key={`paired-${book}`} className={`bc-odds-chip${isBestOpp ? ' is-best' : ''}`}>
                                        {BOOKMAKER_LOGOS[book]
                                          ? <img src={BOOKMAKER_LOGOS[book]} alt={book} className="bc-odds-chip-logo" data-tooltip={book} />
                                          : <span className="bc-odds-chip-name">{book}</span>}
                                        <span className={typeof data.opposing_odds === 'number' ? 'bc-odds-chip-val' : 'bc-num-muted'}>
                                          {typeof data.opposing_odds === 'number' ? data.opposing_odds.toFixed(2) : '—'}
                                        </span>
                                      </div>
                                    )
                                  })}
                                </>}
                              </div>
                              </div>
                            )}

                            {/* Stats */}
                            <div className="bc-expanded-stats">
                              {typeof opp.fair_odds === 'number' && (
                                <div className="bc-expanded-stat">
                                  <span className="bc-expanded-stat-label">Fair Odds</span>
                                  <span className="bc-expanded-stat-val">{opp.fair_odds.toFixed(2)}</span>
                                </div>
                              )}
                              {typeof opp.win_probability === 'number' && (
                                <div className="bc-expanded-stat">
                                  <span className="bc-expanded-stat-label">Win Probability</span>
                                  <span className="bc-expanded-stat-val">{opp.win_probability.toFixed(2)}%</span>
                                </div>
                              )}
                              {opp.last_update && (
                                <div className="bc-expanded-stat">
                                  <span className="bc-expanded-stat-label">Last Updated</span>
                                  <span className="bc-expanded-stat-val">{formatTimeWithSeconds(opp.last_update)}</span>
                                </div>
                              )}
                            </div>

                          </div>
                        </td>
                      </tr>
                    )
                  })()}
                  </React.Fragment>
                  )
                })}
              </tbody>
            </table>

            {/* Preview fade */}
            {isPreview && <div className="bc-preview-fade" />}
          </div>

          {/* Preview wall */}
          {isPreview && (
            <div style={{ padding: '28px 16px 36px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '520px' }}>
                <PreviewWall
                  title={visibleOpportunities.length > 0 ? `${visibleOpportunities.length} +EV opportunities right now` : '+EV opportunities right now'}
                  features={['Real time odds from all major bookmakers', 'Direct links to each bookmakers game page', 'Automatic multi builder', 'Much more...']}
                  planName="Sports Premium"
                  accent="#60a5fa"
                  hadTrial={hadTrial}
                />
              </div>
            </div>
          )}

          {!isPreview && !loading && visibleOpportunities.length === 0 && (
            <div className="bc-empty">No opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* Pagination — always rendered when more than one page */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bc-pagination">
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
            data-tooltip="First page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
            data-tooltip="Previous page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
            data-tooltip="Next page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
            data-tooltip="Last page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}


      {/* Floating multi FAB — appears once user has selected legs */}
      {!isPreview && selectedLegs.length > 0 && !showMultiModal && (
        <button
          className="bc-multi-fab"
          onClick={() => setShowMultiModal(true)}
          title="Open Multi Builder"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          <span className="bc-multi-fab-label">Multi</span>
          <span className="bc-multi-fab-count">{selectedLegs.length}</span>
        </button>
      )}

      <EVMultiBuilder
        isOpen={showMultiModal}
        onClose={() => setShowMultiModal(false)}
        rawOpportunities={opportunities}
        selectedLegs={selectedLegs}
        onLegsChange={setSelectedLegs}
        kellyFraction={kellyFraction}
        bankroll={bankroll}
        roundStakes={roundStakes}
      />

      <HiddenBetsModal
        isOpen={showHiddenModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpp}
        onUnhideAll={clearAllHidden}
        onClose={() => setShowHiddenModal(false)}
      />

      <TrackBetModal
        isOpen={showTrackModal}
        opp={trackingOpp}
        form={trackForm}
        onFormChange={setTrackForm}
        onConfirm={() => saveTrack(false)}
        onConfirmAndHide={() => saveTrack(true)}
        onClose={closeTrackModal}
      />

      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        kellyFraction={kellyFraction}
        setKellyFraction={setKellyFraction}
        bankroll={bankroll}
        setBankroll={setBankroll}
        roundStakes={roundStakes}
        setRoundStakes={setRoundStakes}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
      />

      {pickerState && (() => {
        const opp = pickerState.opp
        const books = opp.bookmakers && opp.bookmakers.length > 0 ? opp.bookmakers : [opp.bookmaker]
        return (
          <>
            <div className="bc-picker-backdrop" onClick={() => setPickerState(null)} />
            <div className="bc-picker" style={{ top: pickerState.y, left: pickerState.x }}>
              <div className="bc-picker-title">Select Bookmaker</div>
              {books.map(book => (
                <button
                  key={book}
                  className="bc-picker-option"
                  onClick={() => { addLeg(opp, book); setPickerState(null) }}
                >
                  {BOOKMAKER_LOGOS[book]
                    ? <img src={BOOKMAKER_LOGOS[book]} alt={book} className="bc-picker-logo" />
                    : <span style={{ width: 26 }} />}
                  <span className="bc-picker-name">{book}</span>
                </button>
              ))}
            </div>
          </>
        )
      })()}

      <EVTour isVisible={isVisible} isMobile={false} />

      {/* Share Modal */}
      {shareBet && <ShareModal bet={shareBet} onClose={() => setShareBet(null)} />}
    </div>
  )
}
